import React from "react";

// Top-level guard so an unhandled render/lifecycle error shows a readable
// message (and the error text) instead of collapsing the app to a blank
// background. Placed OUTSIDE AuthProvider so it also catches errors thrown
// while reading web storage at startup (e.g. iOS Safari with cookies/storage
// blocked throws SecurityError on localStorage access).
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("App crashed:", error, info);
  }

  handleReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      /* storage may be blocked — ignore */
    }
    window.location.href = "/login";
  };

  render() {
    if (!this.state.error) return this.props.children;

    const { error, info } = this.state;
    // Message FIRST: Safari's error.stack omits the message, and for a React
    // production crash the message holds the error number + args (e.g. the
    // offending object's keys). Component stack names the crashing component.
    const parts = [];
    const msg =
      (error && ((error.name ? error.name + ": " : "") + (error.message || String(error)))) ||
      "Unknown error";
    parts.push(msg);
    if (info && info.componentStack) parts.push("Component stack:" + info.componentStack);
    if (error && error.stack) parts.push(error.stack);
    const details = parts.join("\n\n");

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#091c3c",
          color: "#fff",
          padding: "24px",
          fontFamily: "system-ui, -apple-system, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            maxWidth: 560,
            width: "100%",
            background: "#0d2a5a",
            border: "1px solid #1a3a6a",
            borderRadius: 12,
            padding: 20,
          }}
        >
          <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", marginBottom: 16 }}>
            The app hit an unexpected error. Please screenshot this and send it to support.
          </p>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: 12,
              lineHeight: 1.5,
              background: "#06152e",
              border: "1px solid #1a3a6a",
              borderRadius: 8,
              padding: 12,
              color: "#f4cc1e",
              maxHeight: 360,
              overflow: "auto",
              margin: 0,
            }}
          >
            {details}
          </pre>
          <button
            onClick={this.handleReset}
            style={{
              marginTop: 16,
              background: "#f4cc1e",
              color: "#091c3c",
              border: "none",
              borderRadius: 8,
              padding: "10px 16px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Reset &amp; reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
